import { Controller } from "@hotwired/stimulus";
import confetti from "canvas-confetti";

const MILESTONES = {
  50: { emoji: "🚗", title: "Mo Apprentice" },
  100: { emoji: "💯", title: "Mo Regular" },
  250: { emoji: "🔥", title: "Mo Power User" },
  500: { emoji: "⚡", title: "Mo Expert" },
  1000: { emoji: "🏆", title: "Mo Legend" },
};

const DEFAULT_MILESTONE = { emoji: "🎉", title: "Mo Milestone" };

export default class extends Controller {
  static targets = ["emoji", "title", "body", "message"];
  static values = { count: Number, message: String };

  #rafId = null;
  #canvas = null;
  #confettiFn = null;

  connect() {
    const milestone = MILESTONES[this.countValue] ?? DEFAULT_MILESTONE;

    const n = this.countValue;
    this.emojiTarget.textContent = milestone.emoji;
    this.titleTarget.textContent = milestone.title;
    this.bodyTarget.textContent = `Congratulations, you've sent ${n} messages to Mo!`;
    if (this.messageValue && this.hasMessageTarget) {
      this.messageTarget.textContent = this.messageValue;
    } else if (this.hasMessageTarget) {
      this.messageTarget.hidden = true;
    }

    this.#celebrate();
  }

  disconnect() {
    this.#stopConfetti();
  }

  dismiss() {
    this.#stopConfetti();
    this.element.classList.add(
      "opacity-0",
      "transition-opacity",
      "duration-500",
    );
    setTimeout(() => this.element.remove(), 500);
  }

  #celebrate() {
    const panel =
      document.querySelector(
        "[data-controller~='admin--massimo--chat-submit']",
      ) || document.body;

    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:40;";
    panel.style.position = "relative";
    panel.appendChild(canvas);
    this.#canvas = canvas;

    this.#confettiFn = confetti.create(canvas, {
      resize: true,
      useWorker: false,
    });

    // Emit for 2s, taper over the last 0.5s, then let particles fall naturally for 2s before removing canvas.
    const emitDuration = 2000;
    const taperDuration = 500;
    const start = Date.now();
    const emitEnd = start + emitDuration;
    const taperEnd = emitEnd + taperDuration;
    const colors = ["#a786ff", "#fd8bbc", "#eca184", "#f8deb1"];

    const frame = () => {
      const now = Date.now();
      if (now > taperEnd) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
        // Let in-flight particles finish, then fade canvas out and remove it
        canvas.style.transition = "opacity 2s ease-out";
        canvas.style.opacity = "0";
        setTimeout(() => {
          this.#confettiFn?.reset();
          this.#confettiFn = null;
          canvas.remove();
          this.#canvas = null;
        }, 2000);
        return;
      }
      const elapsed = now - start;
      const taper =
        elapsed < emitDuration
          ? 1
          : Math.max(0, 1 - (elapsed - emitDuration) / taperDuration);
      if (Math.random() > taper) {
        this.#rafId = requestAnimationFrame(frame);
        return;
      }
      this.#confettiFn({
        particleCount: 1,
        angle: 310,
        spread: 40,
        startVelocity: 25,
        gravity: 0.8,
        origin: { x: 0, y: 0 },
        colors,
      });
      this.#confettiFn({
        particleCount: 1,
        angle: 230,
        spread: 40,
        startVelocity: 25,
        gravity: 0.8,
        origin: { x: 1, y: 0 },
        colors,
      });
      this.#rafId = requestAnimationFrame(frame);
    };
    this.#rafId = requestAnimationFrame(frame);
  }

  #stopConfetti() {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    if (this.#confettiFn) {
      this.#confettiFn.reset();
      this.#confettiFn = null;
    }
    this.#canvas?.remove();
    this.#canvas = null;
  }
}
